import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadWasmModule } from '../../utils/wasmLoader';
import './MfersTetris.css';

function MfersTetris() {
  const navigate = useNavigate();
  const [loadState, setLoadState] = useState('loading');
  const [message, setMessage] = useState('');
  const [gameStarted, setGameStarted] = useState(false);
  const [score, setScore] = useState(0);
  const [lines, setLines] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const canvasRef = useRef(null);
  const boardRef = useRef(null);
  const wasmRef = useRef(null);
  const gameLoopRef = useRef(null);
  const renderLoopRef = useRef(null);

  // Load WASM module
  useEffect(() => {
    let mounted = true;

    const loadWasm = async () => {
      try {
        console.log('🔄 Loading WASM module...');

        const modulePath = `${process.env.PUBLIC_URL}/wasm/mferstetris/mferstetris.js`;
        const wasmPath = `${process.env.PUBLIC_URL}/wasm/mferstetris/mferstetris_bg.wasm`;

        const wasm = await loadWasmModule(modulePath, wasmPath);

        if (!mounted) return;

        console.log('✅ WASM module loaded successfully!');

        const msg = wasm.get_message();
        setMessage(msg);

        // Create board
        const board = new wasm.TetrisBoard(10, 20);
        console.log(`✅ Created ${board.width}x${board.height} board`);

        // Store references
        boardRef.current = board;
        wasmRef.current = wasm;

        setLoadState('success');
        console.log('✅ Ready to play!');

      } catch (error) {
        console.error('❌ Failed to load WASM:', error);
        setLoadState('error');
      }
    };

    loadWasm();

    return () => {
      mounted = false;
    };
  }, []);

  // Render loop
  useEffect(() => {
    if (!canvasRef.current || !boardRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const board = boardRef.current;

    const cellSize = 25;
    canvas.width = board.width * cellSize;
    canvas.height = board.height * cellSize;

    // Colors for different Tetrominos
    const colors = [
      '#000000', // 0 = empty (black)
      '#00ffff', // 1 = I (cyan)
      '#ffff00', // 2 = O (yellow)
      '#a000f0', // 3 = T (purple)
      '#00ff00', // 4 = S (green)
      '#ff0000', // 5 = Z (red)
      '#0000ff', // 6 = J (blue)
      '#ff7f00', // 7 = L (orange)
    ];

    const render = () => {
      // Draw the board
      for (let y = 0; y < board.height; y++) {
        for (let x = 0; x < board.width; x++) {
          const value = board.get_display_cell(x, y);
          ctx.fillStyle = colors[value] || '#000000';
          ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);

          // Draw grid lines
          ctx.strokeStyle = '#222222';
          ctx.strokeRect(x * cellSize, y * cellSize, cellSize, cellSize);
        }
      }

      // Update UI state
      setScore(board.score);
      setLines(board.lines_cleared);
      setGameOver(board.game_over);

      if (!board.game_over) {
        renderLoopRef.current = requestAnimationFrame(render);
      }
    };

    render();

    return () => {
      if (renderLoopRef.current) {
        cancelAnimationFrame(renderLoopRef.current);
      }
    };
  }, [loadState, gameStarted]);

  // Game loop - move piece down automatically
  useEffect(() => {
    if (!gameStarted || !boardRef.current) return;

    const gameLoop = setInterval(() => {
      if (boardRef.current && !boardRef.current.game_over) {
        boardRef.current.move_down();
      }
    }, 800); // Move down every 800ms

    gameLoopRef.current = gameLoop;

    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [gameStarted]);

  // Keyboard controls
  useEffect(() => {
    if (!gameStarted || !boardRef.current) return;

    const handleKeyDown = (e) => {
      if (!boardRef.current || boardRef.current.game_over) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          boardRef.current.move_left();
          break;
        case 'ArrowRight':
          e.preventDefault();
          boardRef.current.move_right();
          break;
        case 'ArrowDown':
          e.preventDefault();
          boardRef.current.move_down();
          break;
        case 'ArrowUp':
        case ' ':
          e.preventDefault();
          boardRef.current.rotate();
          break;
        case 'Enter':
          e.preventDefault();
          boardRef.current.hard_drop();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [gameStarted]);

  const handleStartGame = () => {
    if (boardRef.current) {
      boardRef.current.clear();
      boardRef.current.spawn_piece();
      setGameStarted(true);
      setGameOver(false);
      setScore(0);
      setLines(0);
    }
  };

  const handleRestart = () => {
    if (gameLoopRef.current) {
      clearInterval(gameLoopRef.current);
    }
    if (renderLoopRef.current) {
      cancelAnimationFrame(renderLoopRef.current);
    }

    // Create new board
    const board = new wasmRef.current.TetrisBoard(10, 20);
    boardRef.current = board;
    board.spawn_piece();

    setGameStarted(true);
    setGameOver(false);
    setScore(0);
    setLines(0);
  };

  const handleBackToMenu = () => {
    navigate('/mferstetris');
  };

  return (
    <div className="mferstetris-game">
      <button className="back-button" onClick={handleBackToMenu}>
        ← Back to Menu
      </button>

      <h1>Mfers Tetris</h1>

      {loadState === 'loading' && (
        <div className="loading-screen">
          <div className="loader"></div>
          <p>Loading Rust/WASM...</p>
        </div>
      )}

      {loadState === 'error' && (
        <div className="error-screen">
          <h2>❌ Failed to Load</h2>
          <p>Could not load WASM module</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      )}

      {loadState === 'success' && (
        <>
          {!gameStarted ? (
            <div className="start-screen">
              <div className="message-box">
                <h3>{message}</h3>
              </div>

              <div className="info-box">
                <p><strong>Controls:</strong></p>
                <p>← → Move Left/Right</p>
                <p>↓ Soft Drop</p>
                <p>↑ or Space: Rotate</p>
                <p>Enter: Hard Drop</p>
              </div>

              <button className="start-button" onClick={handleStartGame}>
                Start Game
              </button>

              <div className="tech-info">
                <p>🦀 Built with Rust + WebAssembly</p>
              </div>
            </div>
          ) : (
            <div className="game-container">
              <div className="game-stats">
                <div className="stat">
                  <span>Score:</span>
                  <span className="stat-value">{score}</span>
                </div>
                <div className="stat">
                  <span>Lines:</span>
                  <span className="stat-value">{lines}</span>
                </div>
              </div>

              <canvas ref={canvasRef} className="tetris-canvas"></canvas>

              <div className="controls-help">
                <p>← → ↓ Move | ↑/Space Rotate | Enter Drop</p>
              </div>
            </div>
          )}

          {gameOver && (
            <div className="gameover-screen">
              <h2>Game Over!</h2>
              <div className="final-stats">
                <p>Final Score: {score}</p>
                <p>Lines Cleared: {lines}</p>
              </div>
              <div className="button-group">
                <button onClick={handleRestart}>Play Again</button>
                <button onClick={handleBackToMenu}>Back to Menu</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default MfersTetris;
